import { useCallback, useRef, useState } from 'react';
import { RegistrationDocument } from '@stax-core/types-omni';

/**
 * Shared gate for required-document progression in OLD self-reg (SPX-2051).
 *
 * A page wires `onMissingRequiredChange` into `<Documents>` and calls
 * `guardProgression()` from its Continue/Submit handler:
 * - returns `true` when no required documents are missing (allow progression)
 * - returns `false` and reveals the inline errors (via `showRequiredErrors`)
 *   when at least one `is_required` document has no uploaded file.
 */
export function useRequiredDocumentGate() {
  // Hold the latest missing-required list in a ref so `guardProgression` always
  // reads the current value when Continue is clicked. A closed-over state value
  // can be stale if the click handler fires before the render that captured the
  // newest `onMissingRequiredChange` (e.g. the same tick the documents resolve),
  // which would incorrectly let a merchant past a missing required document.
  const missingRequiredRef = useRef<RegistrationDocument[]>([]);
  const [showRequiredErrors, setShowRequiredErrors] = useState(false);

  const handleMissingRequiredChange = useCallback(
    (missing: RegistrationDocument[]) => {
      missingRequiredRef.current = missing;
    },
    [],
  );

  const guardProgression = useCallback((): boolean => {
    const missingRequired = missingRequiredRef.current;
    if (missingRequired.length > 0) {
      setShowRequiredErrors(true);
      // surface a breadcrumb so a "stuck merchant" support ticket is diagnosable
      // from the browser console without reading source
      console.info(
        '[documents] progression blocked — missing required documents:',
        missingRequired.map(doc => doc.id),
      );
      return false;
    }
    return true;
  }, []);

  return { showRequiredErrors, handleMissingRequiredChange, guardProgression };
}
